import type React from "react"
import { useEffect } from "react"
import {
    addOrientationChangeListener,
    removeOrientationChangeListener,
} from "@multimediallc/web-utils"
import { classNames } from "../../../messaging/utils"
import styles from "./ConfirmModal.module.scss"

export enum Alignment {
    left = "left",
    center = "center",
    right = "right",
}

export enum ModalButtonColor {
    Delete = "Delete",
    Accept = "Accept",
}

interface ConfirmModalProps {
    isOpen: boolean
    onClose: () => void
    onConfirm: () => void
    title?: string | React.ReactNode
    message: string | React.ReactNode
    confirmText: string
    cancelText?: string | undefined
    hideOverlayOnClick?: boolean
    preventCancel?: boolean
    rotateDismiss?: boolean
    confirmButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>
    cancelButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>
    textAlign?: Alignment
    linkText?: string
    linkUrl?: string
    confirmButtonColor?: ModalButtonColor
    useOldModalLayout?: boolean
}

export function ConfirmModal({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText,
    cancelText,
    hideOverlayOnClick = true,
    preventCancel = false,
    rotateDismiss = false,
    confirmButtonProps,
    cancelButtonProps,
    textAlign = Alignment.center,
    linkText,
    linkUrl,
    confirmButtonColor = ModalButtonColor.Accept,
    useOldModalLayout = false,
}: ConfirmModalProps) {
    useEffect(() => {
        if (!isOpen) return

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Enter") {
                onConfirm()
            }
            if (event.key === "Escape" && !preventCancel) {
                onClose()
            }
        }

        const handleOrientationChange = () => {
            onClose()
        }

        window.addEventListener("keydown", handleKeyDown)
        if (rotateDismiss) {
            addOrientationChangeListener(handleOrientationChange)
        }

        return () => {
            window.removeEventListener("keydown", handleKeyDown)
            removeOrientationChangeListener(handleOrientationChange)
        }
    }, [isOpen, onConfirm, onClose])

    if (!isOpen) return null

    const handleOverlayClick = () => {
        if (hideOverlayOnClick) {
            onClose()
        }
    }

    const handleContentClick = (e: React.MouseEvent) => {
        e.stopPropagation()
    }

    const TEXT_ALIGN_CLASS_MAP = {
        center: styles.textAlignCenter,
        left: styles.textAlignLeft,
        right: styles.textAlignRight,
    }
    const CONFIRM_BUTTON_COLOR_CLASS_MAP = {
        [ModalButtonColor.Delete]: styles.modalButtonDelete,
        [ModalButtonColor.Accept]: styles.modalButtonAccept,
    }

    const textAlignClass = TEXT_ALIGN_CLASS_MAP[textAlign]
    const confirmButtonClass =
        CONFIRM_BUTTON_COLOR_CLASS_MAP[confirmButtonColor]
    return (
        <div
            data-testid="modal-overlay"
            className={classNames(styles.modalOverlay, {
                [styles.oldLayout]: useOldModalLayout,
            })}
            onClick={handleOverlayClick}
        >
            <div
                data-testid="modal-content"
                className={classNames(styles.modalContent, {
                    [styles.oldLayout]: useOldModalLayout,
                })}
                onClick={handleContentClick}
            >
                {title !== undefined && (
                    <h1
                        data-testid="modal-title"
                        className={classNames(
                            `${styles.modalTitle} ${textAlignClass}`,
                            {
                                [styles.oldLayout]: useOldModalLayout,
                            },
                        )}
                    >
                        {title}
                    </h1>
                )}
                <h2
                    data-testid="modal-message"
                    className={classNames(
                        `${styles.modalMessage} ${textAlignClass}`,
                        {
                            [styles.oldLayout]: useOldModalLayout,
                        },
                    )}
                >
                    {message}
                </h2>
                {linkUrl && linkText && (
                    <p
                        data-testid="modal-link-paragraph"
                        className={`${styles.modalLinkParagraph} ${textAlignClass}`}
                    >
                        <a
                            data-testid="modal-link"
                            href={linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {linkText}
                        </a>
                    </p>
                )}
                <div
                    className={classNames(styles.modalButtons, {
                        [styles.oldLayout]: useOldModalLayout,
                    })}
                >
                    {cancelText !== undefined && (
                        <button
                            data-testid="modal-cancel-button"
                            className={classNames(styles.modalButtonCancel, {
                                [styles.oldLayout]: useOldModalLayout,
                            })}
                            onClick={onClose}
                            {...cancelButtonProps}
                        >
                            {cancelText}
                        </button>
                    )}
                    <button
                        data-testid="modal-confirm-button"
                        className={classNames(confirmButtonClass, {
                            [styles.oldLayout]: useOldModalLayout,
                        })}
                        onClick={onConfirm}
                        {...confirmButtonProps}
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>
    )
}
